/**
 * Opens the docked AI side panel (SellerMate W6.3).
 *
 * In the header it opens the panel as it was left and shows how many
 * scheduled briefings are unread. On a grid, pass `context` (page, date range,
 * selected rows) and the panel opens on the Ask tab with those as chips.
 */
import { Sparkles } from 'lucide-react'
import { Button } from '../ui'
import { useApiQuery } from '../../lib/query/useApiQuery'
import { aiApi, aiKeys } from '../../lib/aiApi'
import { openAiPanel, pageRangeContext, type AiContext } from '../../lib/aiPanel'
import { resolveAppWindows, useAppDateRangeControl } from '../../lib/dateRangeContext'
import { usePpcDataThrough } from '../../lib/ppcDataThrough'
import { deriveTabTitle } from '../../tabs/tabIdentity'

export default function AskAiButton({
  context, label = 'Ask AI', showUnread = false,
}: {
  context?: AiContext
  label?: string
  /** Header use: badge the unread scheduled briefings (polled every 5 min). */
  showUnread?: boolean
}) {
  const briefings = useApiQuery(aiKeys.briefings(), aiApi.briefings, {
    poll: 5 * 60_000, enabled: showUnread, retry: 0,
  })
  const unread = showUnread ? briefings.data?.unread ?? 0 : 0
  // C10: on a page that shows the date range, the question carries the page
  // and the window on screen, unless the grid passed its own context. The
  // page title is read at click time from the address bar (always current).
  const ctl = useAppDateRangeControl()
  const dataThrough = usePpcDataThrough()
  const onScreen = ctl.active ? resolveAppWindows(ctl.range, ctl.compare, ctl.clampTo, dataThrough).effective : null
  const sentContext = (): AiContext | undefined => {
    if (!onScreen) return context
    const page = deriveTabTitle(globalThis.location?.pathname ?? '', globalThis.location?.search ?? '')
    return { ...pageRangeContext(page, onScreen), ...context }
  }
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        const sent = sentContext()
        openAiPanel(context ? { context: sent } : unread > 0 ? { tab: 'briefings' } : sent ? { context: sent } : {})
      }}
      aria-label={unread > 0 ? `${label} (${unread} new briefings)` : label}
      title={context || onScreen ? 'Ask the AI assistant about what you are looking at' : 'Open the AI assistant'}
    >
      <Sparkles size={14} className="text-accent" aria-hidden />
      {label}
      {unread > 0 && (
        <span className="ml-0.5 px-1.5 rounded-full text-[10px] font-semibold leading-4 bg-accent text-accent-fg tabular-nums">
          {unread}
        </span>
      )}
    </Button>
  )
}
